import React, { useEffect, useState } from 'react';

import { ActionIcon, Badge, Box, Center, Group, Loader, Stack, Text } from '@mantine/core';
import { IconAdjustmentsHorizontal } from '@tabler/icons-react';
import KiloSelect from 'components/KiloSelect/KiloSelect';
import LeaderboardBoard from 'components/Leaderboard/LeaderboardBoard';
import { getMovementLabel, MovementWithSection } from 'components/Leaderboard/movementLabel';
import { useIsLargeDisplay } from 'components/Workout/whiteboardDisplay';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import useWorkoutLeaderboard from 'hooks/workoutLeaderboard/useWorkoutLeaderboard';
import { WORKOUT_LEVEL_LABEL_MAP, WorkoutLevelEnum } from 'types/enums';

import LeaderboardFilterSheet, { LeaderboardAppliedFilters } from './LeaderboardFilterSheet';

const DEFAULT_FILTERS: LeaderboardAppliedFilters = { session: 'this-session', gender: 'all', level: 'all' };

const formatMovementOption = (movement: MovementWithSection): string => {
  const { title, meta } = getMovementLabel(movement);
  return `${title} (${meta})`;
};

interface LeaderboardPanelProps {
  workoutId: string;
  movements: MovementWithSection[];
  sessionId: string;
  highlightedAthleteId: string | null;
  onEntryClick: (athleteId: string) => void;
  spacious?: boolean;
}

const LeaderboardPanel = ({
  workoutId,
  movements,
  sessionId,
  highlightedAthleteId,
  onEntryClick,
  spacious = false,
}: LeaderboardPanelProps): JSX.Element => {
  const [selectedMovementId, setSelectedMovementId] = useState<string | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<LeaderboardAppliedFilters>(DEFAULT_FILTERS);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const { trackLeaderboardFilterSheetOpened, trackLeaderboardEntryClicked } = useStaffTracking();
  const isDesktop = useIsLargeDisplay();

  useEffect(() => {
    if (selectedMovementId || movements.length === 0) return;
    setSelectedMovementId(movements[0].workoutSectionMovementId);
  }, [movements, selectedMovementId]);

  const selectedMovement = movements.find(m => m.workoutSectionMovementId === selectedMovementId) ?? null;

  const { data, isLoading } = useWorkoutLeaderboard({
    workout: workoutId,
    workoutSectionMovement: selectedMovementId ?? undefined,
    sessionId: appliedFilters.session === 'this-session' ? sessionId : undefined,
    gender: appliedFilters.gender,
    level: appliedFilters.level,
    enabled: !filterSheetOpen,
  });
  const leaderboard = data?.data;

  const handleEntryClick = (athleteId: string): void => {
    onEntryClick(athleteId);
    const rank = leaderboard?.groups
      .flatMap(group => group.entries)
      .find(e => e.athleteId === athleteId)?.rank;
    if (rank !== undefined) trackLeaderboardEntryClicked({ athlete_id: athleteId, rank });
  };

  if (filterSheetOpen && selectedMovement) {
    return (
      <Box p='md'>
        <LeaderboardFilterSheet
          movement={selectedMovement}
          appliedFilters={appliedFilters}
          onApply={filters => {
            setAppliedFilters(filters);
            setFilterSheetOpen(false);
          }}
          onCancel={() => setFilterSheetOpen(false)}
        />
      </Box>
    );
  }

  const filterChips: string[] = [appliedFilters.session === 'today' ? 'Today' : 'This Session'];
  if (appliedFilters.gender !== 'all') filterChips.push(appliedFilters.gender === 'men' ? 'Men' : 'Women');
  if (appliedFilters.level !== 'all')
    filterChips.push(WORKOUT_LEVEL_LABEL_MAP[appliedFilters.level as WorkoutLevelEnum]);

  return (
    <Box p={isDesktop ? 'lg' : 'md'}>
      <Stack spacing={isDesktop ? 'lg' : 'md'}>
        <Stack spacing={6}>
          <Text size={isDesktop ? 'sm' : 'xs'} weight={700} color='dimmed' transform='uppercase'>
            Scored Item
          </Text>
          <KiloSelect
            data={movements.map(m => ({
              value: m.workoutSectionMovementId,
              label: formatMovementOption(m),
            }))}
            value={selectedMovementId}
            onChange={setSelectedMovementId}
            size={isDesktop ? 'md' : 'sm'}
          />
          {!!leaderboard?.sectionMovement.unit && (
            <Badge
              size={isDesktop ? 'sm' : 'xs'}
              variant='outline'
              color='gray'
              sx={{ alignSelf: 'flex-start' }}
            >
              {leaderboard.sectionMovement.unit}
            </Badge>
          )}
        </Stack>

        <Group position='apart' noWrap>
          <Group spacing={6}>
            <Text size={isDesktop ? 'sm' : 'xs'} weight={700} color='dimmed' transform='uppercase'>
              All Athletes
            </Text>
            {filterChips.map(chip => (
              <Badge key={chip} size={isDesktop ? 'sm' : 'xs'} color='blue' variant='light'>
                {chip}
              </Badge>
            ))}
          </Group>
          <ActionIcon
            size={isDesktop ? 'lg' : 'md'}
            variant='subtle'
            aria-label='Filter leaderboard'
            onClick={() => {
              trackLeaderboardFilterSheetOpened();
              setFilterSheetOpen(true);
            }}
          >
            <IconAdjustmentsHorizontal size={isDesktop ? 22 : 18} />
          </ActionIcon>
        </Group>

        {isLoading ? (
          <Center py='lg'>
            <Loader size={isDesktop ? 'md' : 'sm'} />
          </Center>
        ) : (
          <LeaderboardBoard
            groups={leaderboard?.groups ?? []}
            highlightedAthleteId={highlightedAthleteId}
            onEntryClick={handleEntryClick}
            enlarged={isDesktop}
            spacious={spacious}
            emptyMessage={
              filterChips.length > 1
                ? 'No results match these filters.'
                : 'The leaderboard starts with the first score.'
            }
          />
        )}

        {!!leaderboard?.total && (
          <Text size={isDesktop ? 'sm' : 'xs'} color='dimmed' align='center'>
            {leaderboard.total} athlete{leaderboard.total === 1 ? '' : 's'}
          </Text>
        )}
      </Stack>
    </Box>
  );
};

export default LeaderboardPanel;
